"use client";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import { useGamePageOldAnalytics } from "@/context/AnalyticsProvider/gamePageOld";
import { cn } from "@/lib/utils";
import { type VariantProps, cva } from "class-variance-authority";
import Link from "next/link";
import type { ComponentProps } from "react";

const glassButtonVariants = cva(
  "block flex items-center justify-center size-12 rounded-full backdrop-blur-[20px] bg-white/5 glass-button-border active:scale-90 transition-transform duration-200",
  {
    variants: {
      variant: {
        icon: "[&>*]:hover:scale-[1.2] [&>*]:transition-all [&>*]:duration-200",
        cta: "group relative overflow-hidden",
      },
    },
    defaultVariants: {
      variant: "icon",
    },
  },
);

type GlassButtonProps = {
  label?: string;
  subLabel?: string;
  labelsClassName?: string;
  gameId?: string;
  gameName?: string;
  control?: string;
} & VariantProps<typeof glassButtonVariants> &
  (ComponentProps<"button"> | ComponentProps<typeof Link>);

export const GlassButton = ({
  label,
  subLabel,
  labelsClassName,
  gameId,
  gameName,
  control,
  variant,
  ...buttonProps
}: GlassButtonProps) => {
  const { track } = useGamePageOldAnalytics();
  const { children, className, ...rest } = buttonProps;

  const handleClick = (
    e:
      | React.MouseEvent<HTMLButtonElement>
      | React.MouseEvent<HTMLAnchorElement>,
  ) => {
    if (gameId && control) {
      track({
        action: Action.PRESSED,
        extras: {
          id: `${control.toLowerCase()}`,
          control,
          controlType: "href" in rest ? ControlType.LINK : ControlType.BUTTON,
          controlLabel: label || subLabel,
          gameId,
          gameName,
          version: "v3",
        },
      });
    }
    if ("onClick" in rest && rest.onClick) {
      rest.onClick(e as never);
    }
  };

  const baseProps = {
    className: cn(glassButtonVariants({ variant }), className),
  };

  const content = (
    <>
      {children}
      {variant === "cta" && (
        <span
          aria-hidden
          className={cn(
            "pointer-events-none absolute inset-0",
            "opacity-0 group-hover:opacity-100",
            "transition-opacity duration-300 ease-in-out",
            "bg-[radial-gradient(60%_60%_at_50%_100%,_rgba(var(--accent-colour),_0.40)_0%,_rgba(var(--accent-colour),_0.20)_30%,_rgba(var(--accent-colour),_0.10)_50%,_transparent_80%)]",
          )}
        />
      )}
    </>
  );

  return (
    <span className="block space-y-2 text-xs text-neutral-50 font-suisse text-center w-fit">
      {"href" in rest ? (
        <Link
          {...baseProps}
          {...(rest as ComponentProps<typeof Link>)}
          onClick={handleClick}
        >
          {content}
        </Link>
      ) : (
        <button
          {...baseProps}
          {...(rest as ComponentProps<"button">)}
          onClick={handleClick}
        >
          {content}
        </button>
      )}

      <span className={cn("block", labelsClassName)}>
        {/* continue to take vertical space if empty strings are provided */}
        <p className="whitespace-nowrap">{label === "" ? <br /> : label}</p>
        <p className="whitespace-nowrap font-bold">
          {subLabel === "" ? <br /> : subLabel}
        </p>
      </span>
    </span>
  );
};
